/* O cache de leitura de UMA jornada de hotel: a busca corrente e os detalhes já
   lidos, para a lista, a Página do Hotel e a gaveta lerem a MESMA resposta. O
   kit não faz IO: cada leitura chega como `begin(signal)` do host, e os tipos
   da busca (`S`) e do detalhe (`D`) são os do host. */

type CachedRead<T> = Promise<T> | T;

export interface HotelReadCache<S, D> {
  searches: Map<string, CachedRead<S>>;
  details: Map<string, CachedRead<D>>;
  activeSearch: { key: string; controller: AbortController } | null;
  detailControllers: Map<string, AbortController>;
  /** Quem já leu um detalhe e segue montado (o `useHotelDetails` do genui-kit): avisado pelo
   *  `offerId` quando um detalhe RESOLVIDO é reescrito por fora
   *  (`rewriteHotelDetails`). Sem isto a Página do Hotel que fica montada sob
   *  as camadas do host guardava a leitura antiga até remontar. Também avisado
   *  quando um detalhe RESOLVE ou é semeado: a lista o lê para saber qual hotel
   *  ficou sem quarto (`useSoldOutOfferIds`, #4469). */
  detailListeners: Set<(offerId: string) => void>;
}

export function createHotelReadCache<S, D>(): HotelReadCache<S, D> {
  return {
    searches: new Map(),
    details: new Map(),
    activeSearch: null,
    detailControllers: new Map(),
    detailListeners: new Set(),
  };
}

/** Chama o host já dentro do mundo das promises: um throw síncrono e uma
 *  rejeição não-Error viram a mesma falha tipada que os handlers de eviction
 *  esperam — o chamador nunca precisa de try/catch em volta da chamada. */
async function beginRead<T>(begin: () => Promise<T>, fallbackMessage: string): Promise<T> {
  try {
    return await begin();
  } catch (error) {
    throw error instanceof Error ? error : new Error(fallbackMessage);
  }
}

/** A busca da `key`: a mesma em voo ou já resolvida é reaproveitada; uma busca
 *  de outra `key` aborta a anterior, porque só uma busca está em cena. */
export function loadHotelSearch<S, D>(
  cache: HotelReadCache<S, D>,
  key: string,
  begin: (signal: AbortSignal) => Promise<S>,
): CachedRead<S> {
  const cached = cache.searches.get(key);
  if (cached) return cached;

  if (cache.activeSearch) {
    const active = cache.activeSearch;
    active.controller.abort();
    cache.searches.delete(active.key);
    cache.activeSearch = null;
  }

  const controller = new AbortController();
  const request = beginRead(() => begin(controller.signal), 'Hotel search failed');
  const pending = request.then(
    (result) => {
      if (cache.searches.get(key) === pending) cache.searches.set(key, result);
      if (cache.activeSearch?.controller === controller) cache.activeSearch = null;
      return result;
    },
    (error: unknown) => {
      if (cache.searches.get(key) === pending) cache.searches.delete(key);
      if (cache.activeSearch?.controller === controller) cache.activeSearch = null;
      throw error;
    },
  );
  cache.searches.set(key, pending);
  cache.activeSearch = { key, controller };
  return pending;
}

/** O detalhe da oferta: leituras de ofertas diferentes correm juntas, e a da
 *  mesma oferta colapsa na que está em voo. */
export function loadHotelDetails<S, D>(
  cache: HotelReadCache<S, D>,
  offerId: string,
  begin: (signal: AbortSignal) => Promise<D>,
): CachedRead<D> {
  const cached = cache.details.get(offerId);
  if (cached) return cached;

  const controller = new AbortController();
  const request = beginRead(() => begin(controller.signal), 'Hotel details failed');
  const pending = request.then(
    (result) => {
      if (cache.details.get(offerId) === pending) {
        cache.details.set(offerId, result);
        notifyDetail(cache, offerId);
      }
      if (cache.detailControllers.get(offerId) === controller) {
        cache.detailControllers.delete(offerId);
      }
      return result;
    },
    (error: unknown) => {
      if (cache.details.get(offerId) === pending) cache.details.delete(offerId);
      if (cache.detailControllers.get(offerId) === controller) {
        cache.detailControllers.delete(offerId);
      }
      throw error;
    },
  );
  cache.details.set(offerId, pending);
  cache.detailControllers.set(offerId, controller);
  return pending;
}

/** Semeia o cache com um detalhe já em mãos — a reconstrução por handle (aba
 *  nova) traz `offer` + `detail` juntos do BFF, então gravamos o `detail` aqui
 *  para o `useHotelDetails` da Página do Hotel resolver `ready` de imediato, sem
 *  um segundo round-trip. Sobrescreve qualquer pendência anterior do mesmo id. */
export function primeHotelDetails<S, D>(
  cache: HotelReadCache<S, D>,
  offerId: string,
  detail: D,
): void {
  cache.detailControllers.get(offerId)?.abort();
  cache.detailControllers.delete(offerId);
  cache.details.set(offerId, detail);
  notifyDetail(cache, offerId);
}

/** Reescreve um detalhe RESOLVIDO e avisa quem o lê — uma leitura mais fresca
 *  de uma tarifa (o quote) corrigindo a da lista. Detalhe pendente ou ausente,
 *  ou `rewrite` que devolve `undefined`: no-op, não há leitura na tela a
 *  corrigir. */
export function rewriteHotelDetails<S, D>(
  cache: HotelReadCache<S, D>,
  offerId: string,
  rewrite: (detail: D) => D | undefined,
): void {
  const cached = cache.details.get(offerId);
  if (cached === undefined || cached instanceof Promise) return;
  const next = rewrite(cached);
  if (next === undefined) return;
  cache.details.set(offerId, next);
  notifyDetail(cache, offerId);
}

function notifyDetail<S, D>(cache: HotelReadCache<S, D>, offerId: string): void {
  for (const listener of cache.detailListeners) listener(offerId);
}

export function disposeHotelReadCache<S, D>(cache: HotelReadCache<S, D>): void {
  cache.activeSearch?.controller.abort();
  for (const controller of cache.detailControllers.values()) controller.abort();
  cache.searches.clear();
  cache.details.clear();
  cache.activeSearch = null;
  cache.detailControllers.clear();
  cache.detailListeners.clear();
}
